import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { MagneticButton } from '@/components/site/MagneticButton';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SITE } from '@/data/site';
import { motionOn, finePointer } from '@/lib/motion';

/**
 * Closing call-to-action: oversized centred headline over a violet orb that
 * drifts toward the cursor. The last word of `headline` (or `accent`) takes
 * the serif accent.
 */
export function CTABand({
  eyebrow = 'Got a brief?',
  headline = 'Let’s build the next one together.',
  accent,
  sub = 'Discovery calls are 30 minutes, free, and on WhatsApp. Bring rough thoughts—we’ll bring sharp questions.',
  className = '',
}) {
  const sec = useRef(null);
  const orb = useRef(null);
  const words = headline.split(' ');
  const acc = accent || words[words.length - 1];

  useEffect(() => {
    if (!finePointer() || !motionOn() || !sec.current) return undefined;
    const xTo = gsap.quickTo(orb.current, 'x', { duration: 1.4, ease: 'power3' });
    const yTo = gsap.quickTo(orb.current, 'y', { duration: 1.4, ease: 'power3' });
    const el = sec.current;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      xTo((e.clientX - r.left - r.width / 2) * 0.35);
      yTo((e.clientY - r.top - r.height / 2) * 0.35);
    };
    el.addEventListener('pointermove', onMove);
    return () => el.removeEventListener('pointermove', onMove);
  }, []);

  return (
    <section
      ref={sec}
      data-testid="cta-band"
      className={`relative overflow-hidden border-t border-white/[0.08] py-[clamp(110px,14vw,220px)] ${className}`}
    >
      <div
        ref={orb}
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[min(60vw,900px)] w-[min(60vw,900px)] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[30px]"
        style={{ background: 'radial-gradient(circle, rgba(108,92,231,0.42), rgba(75,159,255,0.12) 45%, transparent 68%)' }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex flex-col items-center text-center">
        <Reveal><span className="nd-mono">— {eyebrow}</span></Reveal>
        <WordReveal
          as="h2"
          text={headline}
          accent={acc}
          className="mt-7 mb-8 max-w-[14ch] font-display font-semibold text-[clamp(48px,9vw,164px)] leading-[0.9] tracking-[-0.055em]"
        />
        <Reveal delay={120}>
          <p className="mx-auto mb-11 max-w-[46ch] text-lg text-[#C9C9D3] leading-relaxed">{sub}</p>
        </Reveal>
        <Reveal delay={200} className="flex flex-wrap justify-center gap-3.5">
          <MagneticButton href={SITE.whatsapp} external variant="primary" data-testid="cta-band-whatsapp">
            Book discovery call
          </MagneticButton>
          <MagneticButton href={`mailto:${SITE.email}`} variant="ghost" data-testid="cta-band-email">
            {SITE.email}
          </MagneticButton>
        </Reveal>
      </div>
    </section>
  );
}
